// src/pages/customer-panel/personalize/PersonalizeVoicePage.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, IconButton, Chip, CircularProgress, Alert, Snackbar, Tooltip, Checkbox, Collapse,
    TextField, InputAdornment, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
    LinearProgress, FormControl,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PublicIcon from '@mui/icons-material/Public';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const LANGUAGE_OPTIONS = [
    { code: 'en-US', name: 'English (US)' },
    { code: 'en-GB', name: 'English (UK)' },
    { code: 'ur-PK', name: 'Urdu' },
    { code: 'ar-SA', name: 'Arabic' },
    { code: 'es-ES', name: 'Spanish' },
    { code: 'fr-FR', name: 'French' },
    { code: 'de-DE', name: 'German' },
    { code: 'hi-IN', name: 'Hindi' },
    { code: 'zh-CN', name: 'Chinese (Mandarin)' },
];

const convertMarkdownToHtml = (md) => {
    if (!md) return '';
    let h = md;
    h = h.replace(/([^\n])(#{1,6}\s)/g, '$1\n\n$2');
    h = h.replace(/^######\s+(.*$)/gim, '<h6>$1</h6>');
    h = h.replace(/^#####\s+(.*$)/gim, '<h5>$1</h5>');
    h = h.replace(/^####\s+(.*$)/gim, '<h4>$1</h4>');
    h = h.replace(/^###\s+(.*$)/gim, '<h3>$1</h3>');
    h = h.replace(/^##\s+(.*$)/gim, '<h2>$1</h2>');
    h = h.replace(/^#\s+(.*$)/gim, '<h1>$1</h1>');
    h = h.replace(/\*\*([^*]+)\*\*/gim, '<strong>$1</strong>');
    h = h.replace(/__([^_]+)__/gim, '<strong>$1</strong>');
    h = h.replace(/\*([^*]+)\*/gim, '<em>$1</em>');
    h = h.replace(/`([^`]+)`/gim, '<code>$1</code>');
    h = h.replace(/^\* (.+)$/gim, '<li>$1</li>');
    h = h.replace(/^- (.+)$/gim, '<li>$1</li>');
    h = h.replace(/\n\n/g, '</p><p>');
    h = h.replace(/\n/g, '<br />');
    if (!h.startsWith('<h') && !h.startsWith('<p') && !h.startsWith('<ul')) h = '<p>' + h + '</p>';
    return h;
};

// ── Processing Dialog ──
function ProcessingDialog({ open, items, action, onClose, title }) {
    const [current, setCurrent] = useState(0);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!open || !items.length) return;
        setCurrent(0); setDone(false);
        let cancelled = false;
        (async () => {
            for (let i = 0; i < items.length; i++) {
                if (cancelled) break;
                try { await action(items[i].id); } catch { /* continue */ }
                setCurrent(i + 1);
            }
            setDone(true);
        })();
        return () => { cancelled = true; };
    }, [open, items, action]);

    if (!open) return null;
    return (
        <Dialog open maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>{title}</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily, mb: 2, color: '#64748b' }}>Processing {current} of {items.length}...</Typography>
                <LinearProgress variant="determinate" value={items.length ? Math.round((current / items.length) * 100) : 0}
                    sx={{ height: 8, borderRadius: 4, bgcolor: `${GREEN_ACCENT}20`, '& .MuiLinearProgress-bar': { bgcolor: GREEN_ACCENT } }} />
                {done && <Alert severity="success" sx={{ mt: 2, fontFamily, borderRadius: '10px' }}>All items processed!</Alert>}
            </DialogContent>
            {done && <DialogActions sx={{ px: 3, pb: 3 }}><Button onClick={onClose} variant="contained" sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>Done</Button></DialogActions>}
        </Dialog>
    );
}

// ── Edit Transcription Dialog ──
function EditTranscriptionDialog({ open, recording, onClose, onSaved }) {
    const [text, setText] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (open && recording) setText(recording.transcription || ''); }, [open, recording]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await axiosInstance.put(`/employee-voice-recordings/${recording.id}`, { transcription: text });
            onSaved();
        } catch { /* handled by parent */ }
        setSaving(false);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Edit Transcription</DialogTitle>
            <DialogContent>
                <TextField fullWidth multiline minRows={10} maxRows={18} value={text} onChange={e => setText(e.target.value)}
                    sx={{ mt: 1, fontFamily, '& .MuiInputBase-root': { fontFamily, fontSize: '0.85rem' } }} />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} variant="contained"
                    sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Expanded Row ──
function ExpandedRow({ recording }) {
    const summaryHtml = useMemo(() => convertMarkdownToHtml(recording.summary), [recording.summary]);
    const transcriptionHtml = useMemo(() => convertMarkdownToHtml(recording.transcription), [recording.transcription]);

    const formatDate = d => { try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return '--'; } };

    return (
        <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid', borderColor: 'grey.100' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <Paper sx={{ p: 2, borderRadius: '10px' }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem', color: '#64748b', mb: 1 }}>Recording Details</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <Box><Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>Filename</Typography><Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600 }}>{recording.original_filename || recording.filename}</Typography></Box>
                        <Box><Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>Language</Typography><Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600 }}>{recording.language_name || '--'}</Typography></Box>
                        <Box><Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>Duration</Typography><Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600 }}>{recording.duration_seconds ? `${Math.round(recording.duration_seconds)}s` : '--'}</Typography></Box>
                        <Box><Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>Upload Date</Typography><Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600 }}>{formatDate(recording.upload_date)}</Typography></Box>
                    </Box>
                </Paper>
            </Box>
            <Paper sx={{ p: 2, borderRadius: '10px', mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem', color: '#64748b', mb: 1 }}>Summary</Typography>
                <Box sx={{ fontFamily, fontSize: '0.8rem', color: '#475569', maxHeight: 120, overflowY: 'auto', '& h1,& h2,& h3': { fontSize: '0.9rem', fontWeight: 700, my: 0.5 }, '& p': { my: 0.5 } }}
                    dangerouslySetInnerHTML={{ __html: summaryHtml || '<p style="color:#94a3b8">No summary available</p>' }} />
            </Paper>
            <Paper sx={{ p: 2, borderRadius: '10px', mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem', color: '#64748b', mb: 1 }}>Transcription</Typography>
                <Box sx={{ fontFamily, fontSize: '0.8rem', color: '#475569', maxHeight: 200, overflowY: 'auto', '& h1,& h2,& h3': { fontSize: '0.9rem', fontWeight: 700, my: 0.5 }, '& p': { my: 0.5 } }}
                    dangerouslySetInnerHTML={{ __html: transcriptionHtml || '<p style="color:#94a3b8">No transcription</p>' }} />
            </Paper>
            {recording.audio_url && (
                <Paper sx={{ p: 2, borderRadius: '10px' }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem', color: '#64748b', mb: 1 }}>Audio Player</Typography>
                    <audio controls style={{ width: '100%' }}>
                        <source src={recording.audio_url} type="audio/webm" />
                        <source src={recording.audio_url} type="audio/mpeg" />
                    </audio>
                </Paper>
            )}
        </Box>
    );
}

// ── Main Page ──
export default function PersonalizeVoicePage() {
    const [recordings, setRecordings] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading, setLoading] = useState(true);

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [expandedId, setExpandedId] = useState(null);
    const [editRecording, setEditRecording] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [processingState, setProcessingState] = useState({ open: false, items: [], action: null, title: '' });

    // Recording state
    const [isRecording, setIsRecording] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [selectedLang, setSelectedLang] = useState(LANGUAGE_OPTIONS[0]);
    const maxMinutes = 2;
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);

    const showMsg = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    useEffect(() => { document.title = 'Personalize Voice | Customer Panel'; }, []);

    // Load recordings
    const loadRecordings = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axiosInstance.get('/employee-voice-recordings', { params: { page, limit, search: searchTerm, status: statusFilter } });
            setRecordings(data.recordings || []); setTotal(data.total || 0);
        } catch { setRecordings([]); setTotal(0); }
        setLoading(false);
    }, [page, limit, searchTerm, statusFilter]);

    useEffect(() => { loadRecordings(); }, [loadRecordings]);

    // Recording controls
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream);
            chunksRef.current = [];
            mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            mr.start();
            mediaRecorderRef.current = mr;
            setIsRecording(true);
            setRecordingTime(0);
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => {
                    if (prev + 1 >= maxMinutes * 60) { stopRecording(); return prev; }
                    return prev + 1;
                });
            }, 1000);
        } catch { showMsg('Could not access microphone', 'error'); }
    };

    const stopRecording = useCallback(() => {
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') { setIsRecording(false); return; }

        mediaRecorderRef.current.onstop = async () => {
            const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
            mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
            setIsRecording(false);
            setIsUploading(true);
            try {
                const fd = new FormData();
                fd.append('file', blob, `recording_${Date.now()}.webm`);
                fd.append('language_code', selectedLang.code);
                fd.append('language_name', selectedLang.name);
                await axiosInstance.post('/employee-voice-recordings/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                showMsg('Recording uploaded successfully!'); loadRecordings();
            } catch (err) { showMsg(err.response?.data?.message || 'Failed to upload recording', 'error'); }
            setIsUploading(false);
        };
        mediaRecorderRef.current.stop();
    }, [selectedLang, loadRecordings]);

    const formatTime = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

    // Bulk actions
    const handleBulkTrain = (action) => {
        const sel = recordings.filter(r => selectedIds.has(r.id));
        if (!sel.length) { showMsg('Select at least one recording.', 'error'); return; }
        setProcessingState({
            open: true, items: sel, title: action === 'train' ? 'Training Recordings' : 'Untraining Recordings',
            action: async (id) => { await axiosInstance.post('/employee-voice-recordings/training-status', { recording_ids: [id], is_trained: action === 'train' }); },
        });
    };

    const handleBulkPublic = async (isPublic) => {
        const sel = recordings.filter(r => selectedIds.has(r.id));
        if (!sel.length) { showMsg('Select at least one recording.', 'error'); return; }
        if (isPublic) {
            const untrained = sel.filter(r => !r.is_trained);
            if (untrained.length) { showMsg(`${untrained.length} recording(s) must be trained first.`, 'error'); return; }
        }
        if (!window.confirm(`Mark ${sel.length} recording(s) as ${isPublic ? 'public' : 'private'}?`)) return;
        try {
            await axiosInstance.post('/employee-voice-recordings/is-public', { recording_ids: sel.map(r => r.id), is_public: isPublic });
            showMsg(`Recordings marked as ${isPublic ? 'public' : 'private'}!`); setSelectedIds(new Set()); loadRecordings();
        } catch { showMsg('Failed to update', 'error'); }
    };

    const handleBulkDelete = async () => {
        const sel = recordings.filter(r => selectedIds.has(r.id));
        if (!sel.length) { showMsg('Select at least one recording.', 'error'); return; }
        if (!window.confirm(`Delete ${sel.length} recording(s)? This cannot be undone.`)) return;
        try {
            for (const r of sel) await axiosInstance.delete(`/employee-voice-recordings/${r.id}`);
            showMsg('Recordings deleted!'); setSelectedIds(new Set()); loadRecordings();
        } catch { showMsg('Failed to delete some recordings', 'error'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this recording?')) return;
        try { await axiosInstance.delete(`/employee-voice-recordings/${id}`); showMsg('Recording deleted!'); loadRecordings(); }
        catch { showMsg('Failed to delete', 'error'); }
    };

    const handleRefresh = async (id) => {
        try { await axiosInstance.post(`/employee-voice-recordings/${id}/refresh`); showMsg('Transcription refresh started!'); loadRecordings(); }
        catch { showMsg('Failed to refresh', 'error'); }
    };

    const closeProcessing = () => { setProcessingState({ open: false, items: [], action: null, title: '' }); setSelectedIds(new Set()); loadRecordings(); };
    const toggleSelect = id => setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
    const toggleAll = () => setSelectedIds(prev => prev.size === recordings.length ? new Set() : new Set(recordings.map(r => r.id)));

    const totalPages = Math.ceil(total / limit);
    const formatDate = d => { if (!d) return '--'; try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return '--'; } };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.5rem', md: '2rem' } }}>Personalize Voice</Typography>
                <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>Record and manage your voice recordings</Typography>
            </Box>

            {/* Record Voice Card */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: 'grey.100', textAlign: 'center' }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2 }}>Record Voice</Typography>
                <Box sx={{ maxWidth: 400, mx: 'auto' }}>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <Select value={selectedLang.code} disabled={isRecording || isUploading}
                            onChange={e => { const l = LANGUAGE_OPTIONS.find(x => x.code === e.target.value); if (l) setSelectedLang(l); }}
                            sx={{ fontFamily }}>
                            {LANGUAGE_OPTIONS.map(l => <MenuItem key={l.code} value={l.code} sx={{ fontFamily }}>{l.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Button onClick={isRecording ? stopRecording : startRecording} disabled={isUploading} variant="contained"
                        startIcon={isRecording ? <StopIcon /> : <MicIcon />}
                        sx={{ fontFamily, fontWeight: 600, px: 4, py: 1.5, fontSize: '1rem', borderRadius: '12px', textTransform: 'none',
                            bgcolor: isRecording ? '#ef4444' : GREEN_ACCENT, '&:hover': { bgcolor: isRecording ? '#dc2626' : '#059669' },
                            ...(isRecording ? { animation: 'pulse 1.5s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.7 } } } : {}) }}>
                        {isRecording ? 'Stop Recording' : 'Start Recording'}
                    </Button>
                    {isUploading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
                            <CircularProgress size={18} sx={{ color: GREEN_ACCENT }} />
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: GREEN_ACCENT }}>Processing and transcribing...</Typography>
                        </Box>
                    )}
                    {isRecording && (
                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{ fontFamily, fontSize: '2rem', fontWeight: 700, color: '#ef4444' }}>{formatTime(recordingTime)}</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>Recording in progress...</Typography>
                        </Box>
                    )}
                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', mt: 2 }}>
                        Maximum Recording Time: <strong>{maxMinutes} minute{maxMinutes !== 1 ? 's' : ''}</strong>
                    </Typography>
                </Box>
            </Paper>

            {/* Recordings Table */}
            <Paper sx={{ borderRadius: '16px', border: '1px solid', borderColor: 'grey.100', overflow: 'hidden' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem' }}>My Voice Recordings</Typography>
                    {selectedIds.size > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontFamily, fontWeight: 600, color: GREEN_ACCENT, fontSize: '0.85rem' }}>{selectedIds.size} selected</Typography>
                            <Button size="small" startIcon={<CheckCircleIcon />} onClick={() => handleBulkTrain('train')} sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: '#10b981', color: '#fff', borderRadius: '8px', '&:hover': { bgcolor: '#059669' } }}>Trained</Button>
                            <Button size="small" startIcon={<CancelIcon />} onClick={() => handleBulkTrain('untrain')} sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: '#f59e0b', color: '#fff', borderRadius: '8px', '&:hover': { bgcolor: '#d97706' } }}>Untrained</Button>
                            <Button size="small" startIcon={<PublicIcon />} onClick={() => handleBulkPublic(true)} sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: '#3b82f6', color: '#fff', borderRadius: '8px', '&:hover': { bgcolor: '#2563eb' } }}>Public</Button>
                            <Button size="small" startIcon={<VisibilityOffIcon />} onClick={() => handleBulkPublic(false)} sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: '#64748b', color: '#fff', borderRadius: '8px', '&:hover': { bgcolor: '#475569' } }}>Private</Button>
                            <Button size="small" startIcon={<DeleteIcon />} onClick={handleBulkDelete} sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: '#ef4444', color: '#fff', borderRadius: '8px', '&:hover': { bgcolor: '#dc2626' } }}>Delete</Button>
                            <Button size="small" onClick={() => setSelectedIds(new Set())} sx={{ fontFamily, textTransform: 'none', color: GREEN_ACCENT }}>Clear</Button>
                        </Box>
                    )}
                </Box>

                {/* Search + Filter */}
                <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'grey.100' }}>
                    <TextField size="small" placeholder="Search recordings..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }}
                        sx={{ flex: 1, minWidth: 200, fontFamily }} />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} sx={{ fontFamily }}>
                            <MenuItem value="all" sx={{ fontFamily }}>All</MenuItem>
                            <MenuItem value="trained" sx={{ fontFamily }}>Trained</MenuItem>
                            <MenuItem value="untrained" sx={{ fontFamily }}>Untrained</MenuItem>
                            <MenuItem value="transcribed" sx={{ fontFamily }}>Transcribed</MenuItem>
                            <MenuItem value="pending" sx={{ fontFamily }}>Pending</MenuItem>
                            <MenuItem value="error" sx={{ fontFamily }}>Error</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title="Refresh"><IconButton onClick={loadRecordings} sx={{ color: '#64748b' }}><RefreshIcon /></IconButton></Tooltip>
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell padding="checkbox"><Checkbox size="small" checked={selectedIds.size === recordings.length && recordings.length > 0} indeterminate={selectedIds.size > 0 && selectedIds.size < recordings.length} onChange={toggleAll} sx={{ color: GREEN_ACCENT, '&.Mui-checked': { color: GREEN_ACCENT }, '&.MuiCheckbox-indeterminate': { color: GREEN_ACCENT } }} /></TableCell>
                                <TableCell sx={{ width: 40 }} />
                                {['Recording', 'Status', 'Visibility', 'Metrics', 'Upload Date', 'Actions'].map(h => (
                                    <TableCell key={h} sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={32} sx={{ color: GREEN_ACCENT }} /></TableCell></TableRow>
                            ) : recordings.length === 0 ? (
                                <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                    <MicIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                    <Typography sx={{ fontFamily, color: '#94a3b8' }}>No voice recordings</Typography>
                                </TableCell></TableRow>
                            ) : recordings.map(r => (
                                <React.Fragment key={r.id}>
                                    <TableRow sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell padding="checkbox"><Checkbox size="small" checked={selectedIds.has(r.id)} onChange={() => toggleSelect(r.id)} sx={{ color: GREEN_ACCENT, '&.Mui-checked': { color: GREEN_ACCENT } }} /></TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
                                                {expandedId === r.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <MicIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                                                <Box>
                                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.original_filename || r.filename || '--'}</Typography>
                                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>
                                                        {r.language_name}{r.duration_seconds ? ` \u2022 ${Math.round(r.duration_seconds)}s` : ''}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                {r.is_transcribed ? <Chip label="Transcribed" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#d1fae5', color: '#10b981' }} />
                                                : <Chip label={r.transcription_status === 'pending' ? 'Pending' : 'Not Transcribed'} size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#fef3c7', color: '#f59e0b' }} />}
                                                {r.is_trained && <Chip label="Trained" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#dbeafe', color: '#3b82f6' }} />}
                                                {r.transcription_status === 'error' && <Chip label="Error" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#fee2e2', color: '#ef4444' }} />}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {r.is_trained ? (
                                                r.is_public ? <Chip icon={<PublicIcon sx={{ fontSize: 14 }} />} label="Public" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#dbeafe', color: '#3b82f6' }} />
                                                : <Chip icon={<VisibilityOffIcon sx={{ fontSize: 14 }} />} label="Private" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#f1f5f9', color: '#64748b' }} />
                                            ) : <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem' }}>&mdash;</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>{r.transcription_words || 0} words</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>{r.tokens || 0} tokens</Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>{formatDate(r.upload_date)}</TableCell>
                                        <TableCell>
                                            <Tooltip title="Edit Transcription"><IconButton size="small" onClick={() => setEditRecording(r)} sx={{ color: '#3b82f6', mr: 0.5 }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Refresh Transcription"><IconButton size="small" onClick={() => handleRefresh(r.id)} sx={{ color: GREEN_ACCENT, mr: 0.5 }}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(r.id)} sx={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                                            <Collapse in={expandedId === r.id} timeout="auto" unmountOnExit>
                                                <ExpandedRow recording={r} />
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderTop: '1px solid', borderColor: 'grey.100' }}>
                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                            {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton size="small" disabled={page === 1} onClick={() => setPage(p => p - 1)}><NavigateBeforeIcon /></IconButton>
                            <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><NavigateNextIcon /></IconButton>
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* Dialogs */}
            <EditTranscriptionDialog open={!!editRecording} recording={editRecording} onClose={() => setEditRecording(null)} onSaved={() => { setEditRecording(null); showMsg('Transcription updated!'); loadRecordings(); }} />
            <ProcessingDialog open={processingState.open} items={processingState.items} action={processingState.action} onClose={closeProcessing} title={processingState.title} />

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnackbar(p => ({ ...p, open: false }))} severity={snackbar.severity} sx={{ fontFamily, borderRadius: '10px' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}
